import React, { useEffect, useRef, useState } from "react";
import { Navigate, useNavigate } from "react-router-dom";
import { api, formatError, isValidEmail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import Navbar from "@/components/Navbar";
import { US_STATES } from "@/lib/constants";
import { toast } from "@/lib/toast";
import { AvatarEditor } from "@/pages/Account";
import { Loader2, Building2, ShieldCheck, FileCheck2, ClipboardCheck, ArrowLeft, Check, Upload, Lock, Plus, Trash2, FileText } from "lucide-react";

const HEADING = "text-[#E8E8EA]";
const LABEL = "font-mono text-xs uppercase tracking-wide text-[#8B8D90]";
const INPUT = "mt-1 w-full bg-[#0D0E0F] border border-[#3E3F42] focus:border-[#57682A] outline-none px-3 py-2.5 text-[#E8E8EA] placeholder:text-[#6B6D70] transition-colors";

const OWNERSHIP = ["Corporation", "Partnership", "Sole Proprietorship", "LLC"];
// A business can hold MULTIPLE FFL types (e.g. 07 + 02), so these are checkboxes.
const FFL_TYPES = [
  ["01", "01 — Dealer"],
  ["02", "02 — Pawnbroker"],
  ["03", "03 — Collector (C&R)"],
  ["06", "06 — Ammunition Manufacturer"],
  ["07", "07 — Firearms Manufacturer"],
  ["08", "08 — Importer"],
  ["09", "09 — Destructive Devices Dealer"],
  ["10", "10 — Destructive Devices Manufacturer"],
  ["11", "11 — Destructive Devices Importer"],
];
const FFL_TYPE_LABEL = Object.fromEntries(FFL_TYPES);
// A business may hold more than one NFA SOT class, so these are checkboxes too.
const SOT_CLASS_OPTS = ["Class 1 — Importer (NFA)", "Class 2 — Manufacturer (NFA)", "Class 3 — Dealer (NFA)"];
// Common additional-license types; "Other" reveals a free-text field.
const ADDL_LICENSE_TYPES = [
  "Additional FFL (another premises)",
  "State firearms dealer license",
  "Local business permit",
  "Resale / sales-tax permit",
  "ITAR / DDTC registration",
  "Other",
];

const STEPS = [
  { n: 1, label: "Business & Owner", icon: Building2 },
  { n: 2, label: "FFL / SOT", icon: ShieldCheck },
  { n: 3, label: "Review", icon: ClipboardCheck },
];

const Field = ({ label, optional, children, testid }) => (
  <div data-testid={testid}>
    <label className={LABEL}>{label}{optional && <span className="normal-case text-[#6B6D70]"> (optional)</span>}</label>
    {children}
  </div>
);

const CheckTile = ({ on, label, onClick, testid }) => (
  <button type="button" onClick={onClick} data-testid={testid}
    className={`flex items-center gap-2 border px-3 py-2 text-left transition-colors ${on ? "border-olive-bright bg-olive/10" : "border-[#3E3F42] hover:border-[#57682A]"}`}>
    <span className={`shrink-0 w-4 h-4 flex items-center justify-center border ${on ? "bg-olive-bright border-olive-bright" : "border-[#55585C]"}`}>
      {on && <Check className="w-3 h-3 text-black" strokeWidth={3} />}
    </span>
    <span className="text-xs text-[#E8E8EA] font-body leading-tight">{label}</span>
  </button>
);

export default function BusinessApply() {
  const { user, loading, businessFeaturesEnabled } = useAuth();
  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  const [form, setForm] = useState({
    legal_business_name: "", dba: "", tax_id_ein: "", form_of_ownership: "", owner_name: "", owner_title: "",
    premises_address: "", city: "", state: "", zip_code: "", years_in_business: "", business_phone: "",
    business_email: "", website: "", pct_sales_firearms: "", account_type_requested: "", attestation: false,
    ffl_number: "", expiration_date: "", licensee_name: "",
    ffl_types: [], sot_classes: [],
  });
  const [ffl, setFfl] = useState(null);           // existing primary FFL summary from the server
  const [verifyStatus, setVerifyStatus] = useState(null);  // business_verification_status
  const [appState, setAppState] = useState(null);         // onboarding_state / status
  const [docFile, setDocFile] = useState(null);
  const [addlLicenses, setAddlLicenses] = useState([]);   // additional licenses already on file
  const [newLic, setNewLic] = useState({ license_type: "", license_type_other: "", license_number: "", issuing_authority: "", expiration_date: "" });
  const [newLicFile, setNewLicFile] = useState(null);
  const [addingLic, setAddingLic] = useState(false);
  const [busy, setBusy] = useState(false);
  const [loadingApp, setLoadingApp] = useState(true);
  const [err, setErr] = useState("");
  const fileRef = useRef(null);
  const licFileRef = useRef(null);

  const toggleArr = (key, val) => setForm((f) => {
    const cur = f[key] || [];
    return { ...f, [key]: cur.includes(val) ? cur.filter((x) => x !== val) : [...cur, val] };
  });

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e?.target ? e.target.value : e }));
  const setLic = (k) => (e) => setNewLic((l) => ({ ...l, [k]: e?.target ? e.target.value : e }));
  const isFirearms = form.account_type_requested === "firearms_accessories";

  useEffect(() => {
    if (!user) return;
    api.get("/business/application")
      .then(({ data }) => {
        const a = data.application || {};
        setForm((f) => ({
          ...f,
          legal_business_name: a.legal_business_name || a.business_name || "",
          dba: a.dba || "", tax_id_ein: a.tax_id_ein || "", form_of_ownership: a.form_of_ownership || "",
          owner_name: a.owner_name || "", owner_title: a.owner_title || "",
          premises_address: a.premises_address || "", city: a.city || "", state: a.state || "", zip_code: a.zip_code || "",
          years_in_business: a.years_in_business || "", business_phone: a.business_phone || "",
          business_email: a.business_email || a.email || "", website: a.website || "",
          pct_sales_firearms: a.pct_sales_firearms || "",
          account_type_requested: a.account_type_requested || "", attestation: !!a.attestation,
          ffl_types: Array.isArray(a.ffl_types) ? a.ffl_types : [],
          sot_classes: Array.isArray(a.sot_classes) ? a.sot_classes : [],
          ffl_number: (data.ffl && data.ffl.ffl_number) || "",
          expiration_date: (data.ffl && data.ffl.expiration_date) || "", licensee_name: (data.ffl && data.ffl.licensee_name) || "",
        }));
        setFfl(data.ffl || null);
        setAddlLicenses(Array.isArray(a.additional_licenses) ? a.additional_licenses : []);
        setVerifyStatus(data.business_verification_status || null);
        setAppState({ onboarding_state: a.onboarding_state, status: a.status });
      })
      .catch((e) => setErr(formatError(e?.response?.data?.detail) || "Couldn't load your application."))
      .finally(() => setLoadingApp(false));
  }, [user]);

  const saveStep1 = async () => {
    const need = ["legal_business_name", "tax_id_ein", "form_of_ownership", "owner_name", "owner_title", "premises_address", "city", "state", "zip_code", "business_phone", "business_email"];
    const miss = need.filter((k) => !String(form[k] || "").trim());
    if (miss.length) { setErr("Please complete every required field."); return; }
    if (!isValidEmail(form.business_email)) { setErr("Enter a valid business email."); return; }
    setBusy(true); setErr("");
    try {
      await api.put("/business/application", {
        legal_business_name: form.legal_business_name, dba: form.dba, tax_id_ein: form.tax_id_ein,
        form_of_ownership: form.form_of_ownership, owner_name: form.owner_name, owner_title: form.owner_title,
        premises_address: form.premises_address, city: form.city, state: form.state, zip_code: form.zip_code,
        years_in_business: form.years_in_business, business_phone: form.business_phone,
        business_email: form.business_email, website: form.website,
      });
      setStep(2); window.scrollTo(0, 0);
    } catch (e) { setErr(formatError(e?.response?.data?.detail) || "Couldn't save. Try again."); }
    finally { setBusy(false); }
  };

  const saveStep2 = async () => {
    setErr("");
    if (!form.account_type_requested) { setErr("Choose the account type you're requesting."); return; }
    if (!form.attestation) { setErr("You must certify the attestation to continue."); return; }
    const hasDoc = !!docFile || (ffl && ffl.has_document);
    if (isFirearms) {
      if (!(form.ffl_types || []).length) { setErr("Select at least one FFL type you hold."); return; }
      const need = ["ffl_number", "expiration_date", "licensee_name"];
      const miss = need.filter((k) => !String(form[k] || "").trim());
      if (miss.length || !hasDoc) { setErr("Firearms & Accessories requires your FFL number, licensee name, expiration date and a copy of the license."); return; }
    }
    setBusy(true);
    try {
      // Upload / replace the PRIMARY FFL document when a new file is chosen and the FFL fields are present.
      const primaryType = (form.ffl_types || [])[0] || "";
      const allFflFields = primaryType && ["ffl_number", "expiration_date", "licensee_name"].every((k) => String(form[k] || "").trim());
      if (docFile && allFflFields) {
        const fd = new FormData();
        fd.append("ffl_number", form.ffl_number.trim());
        fd.append("ffl_type", primaryType);
        fd.append("expiration_date", form.expiration_date.trim());
        fd.append("licensee_name", form.licensee_name.trim());
        fd.append("premises_address", `${form.premises_address}, ${form.city}, ${form.state} ${form.zip_code}`.trim());
        fd.append("document", docFile);
        const { data } = await api.post("/me/licenses/ffl", fd, { headers: { "Content-Type": "multipart/form-data" } });
        setFfl({ status: data.status, ffl_number: data.ffl_number, ffl_type: data.ffl_type, expiration_date: data.expiration_date, licensee_name: data.licensee_name, has_document: data.has_document, document_filename: data.document_filename });
        setDocFile(null);
      }
      await api.put("/business/application", {
        ffl_types: form.ffl_types, sot_classes: form.sot_classes, pct_sales_firearms: form.pct_sales_firearms,
        account_type_requested: form.account_type_requested, attestation: form.attestation,
      });
      setStep(3); window.scrollTo(0, 0);
    } catch (e) { setErr(formatError(e?.response?.data?.detail) || "Couldn't save. Try again."); }
    finally { setBusy(false); }
  };

  const submitApp = async () => {
    setBusy(true); setErr("");
    try {
      await api.post("/business/application/submit");
      setAppState({ onboarding_state: "submitted", status: "pending" });
      toast.success("Application submitted for review.");
      window.scrollTo(0, 0);
    } catch (e) { setErr(formatError(e?.response?.data?.detail) || "Couldn't submit. Check your entries."); }
    finally { setBusy(false); }
  };

  const onPickFile = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (f.size > 15 * 1024 * 1024) { setErr("That file is over 15MB."); return; }
    setDocFile(f); setErr("");
  };

  const onPickLicFile = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (f.size > 15 * 1024 * 1024) { setErr("That file is over 15MB."); return; }
    setNewLicFile(f); setErr("");
  };

  const addLicense = async () => {
    const type = newLic.license_type === "Other" ? newLic.license_type_other.trim() : newLic.license_type;
    if (!type) { setErr("Choose or enter the license type to add."); return; }
    setAddingLic(true); setErr("");
    try {
      const fd = new FormData();
      fd.append("license_type", type);
      fd.append("license_number", newLic.license_number || "");
      fd.append("issuing_authority", newLic.issuing_authority || "");
      fd.append("expiration_date", newLic.expiration_date || "");
      if (newLicFile) fd.append("document", newLicFile);
      const { data } = await api.post("/business/application/licenses", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setAddlLicenses((ls) => [...ls, data.license]);
      setNewLic({ license_type: "", license_type_other: "", license_number: "", issuing_authority: "", expiration_date: "" });
      setNewLicFile(null);
      if (licFileRef.current) licFileRef.current.value = "";
      toast.success("License added.");
    } catch (e) { setErr(formatError(e?.response?.data?.detail) || "Couldn't add the license."); }
    finally { setAddingLic(false); }
  };

  const removeLicense = async (id) => {
    try {
      await api.delete(`/business/application/licenses/${id}`);
      setAddlLicenses((ls) => ls.filter((l) => l.license_id !== id));
    } catch (e) { setErr(formatError(e?.response?.data?.detail) || "Couldn't remove the license."); }
  };

  const alreadyDone = appState && (appState.status === "approved" || verifyStatus === "verified" || appState.onboarding_state === "submitted");

  if (loading || loadingApp) {
    return (
      <div className="min-h-screen bg-black"><Navbar />
        <div className="flex items-center justify-center py-32 text-muted-foreground font-mono text-sm uppercase"><Loader2 className="w-5 h-5 animate-spin mr-2" /> Loading…</div>
      </div>
    );
  }
  if (!user) return <Navigate to="/login" replace />;
  if (!businessFeaturesEnabled) return <Navigate to="/feed" replace />;
  const isBiz = user.account_type === "business" || user.is_dealer || user.is_dealer_applicant;
  if (!isBiz) return <Navigate to="/dashboard" replace />;

  return (
    <div className="min-h-screen bg-black text-foreground" data-testid="business-apply-page">
      <Navbar />
      <div className="max-w-2xl mx-auto px-5 py-10">
        <div className="flex items-center gap-2.5 mb-1">
          <Building2 className="w-6 h-6 text-olive-bright" />
          <h1 className={`font-heading text-3xl font-bold uppercase tracking-tight ${HEADING}`}>Business Verification</h1>
        </div>
        <p className="text-sm text-[#8B8D90] font-body mb-7">
          Licensed FFL/SOT dealers and retailers. Complete this application to get your business verified and live on OpticSwap.
        </p>

        {alreadyDone ? (
          <div className="border border-olive/40 bg-olive/5 p-6" data-testid="business-apply-status">
            <ShieldCheck className="w-8 h-8 text-olive-bright mb-3" />
            {appState.status === "approved" || verifyStatus === "verified" ? (
              <>
                <p className={`font-heading text-2xl font-bold ${HEADING}`}>Your business is verified.</p>
                <p className="text-sm text-secondary-foreground mt-2 font-body leading-relaxed">You're approved. The DEALER VERIFIED badge is active on your account.</p>
              </>
            ) : (
              <>
                <p className={`font-heading text-2xl font-bold ${HEADING}`}>Application submitted.</p>
                <p className="text-sm text-secondary-foreground mt-2 font-body leading-relaxed">
                  Thanks — our team is reviewing your business and FFL. We'll email you when it's approved. This usually takes a couple of business days.
                </p>
              </>
            )}
            <button onClick={() => navigate("/dashboard")} data-testid="business-apply-done-dashboard" className="mt-5 inline-flex items-center justify-center bg-olive hover:bg-olive-bright text-white font-heading font-bold uppercase text-sm py-2.5 px-5 transition-colors">Go to dashboard</button>
          </div>
        ) : (
          <>
            {/* Stepper */}
            <div className="flex items-center gap-2 mb-7" data-testid="business-apply-stepper">
              {STEPS.map((s, i) => (
                <React.Fragment key={s.n}>
                  <div className={`flex items-center gap-2 ${step >= s.n ? "text-olive-bright" : "text-[#55585C]"}`}>
                    <span className={`w-7 h-7 flex items-center justify-center border ${step >= s.n ? "border-olive-bright" : "border-[#3E3F42]"} font-mono text-xs`}>
                      {step > s.n ? <Check className="w-3.5 h-3.5" /> : s.n}
                    </span>
                    <span className="font-mono text-[11px] uppercase tracking-wide hidden sm:inline">{s.label}</span>
                  </div>
                  {i < STEPS.length - 1 && <div className={`flex-1 h-px ${step > s.n ? "bg-olive-bright" : "bg-[#3E3F42]"}`} />}
                </React.Fragment>
              ))}
            </div>

            {err && <div data-testid="business-apply-error" className="mb-4 border border-blaze/50 bg-blaze/10 text-blaze text-sm px-3 py-2 font-mono">{err}</div>}

            {step === 1 && (
              <div className="space-y-4" data-testid="business-apply-step-1">
                <div className="border border-[#3E3F42] bg-[#0D0E0F] p-4" data-testid="ba-logo-block">
                  <p className={`${LABEL} mb-1`}>Business logo <span className="normal-case text-[#6B6D70]">(optional)</span></p>
                  <p className="text-xs text-[#6B6D70] mb-3 font-body leading-relaxed">Used as your public profile / storefront avatar once you're verified. Square works best.</p>
                  <AvatarEditor />
                </div>
                <Field label="Legal business name" testid="ba-legal-name"><input className={INPUT} value={form.legal_business_name} onChange={set("legal_business_name")} data-testid="ba-legal-name-input" placeholder="e.g. Ridgeline Arms, LLC" /></Field>
                <Field label="DBA" optional testid="ba-dba"><input className={INPUT} value={form.dba} onChange={set("dba")} data-testid="ba-dba-input" placeholder="Doing-business-as name" /></Field>
                <div className="grid grid-cols-2 gap-3">
                  <Field label="Federal Tax ID / EIN" testid="ba-ein"><input className={INPUT} value={form.tax_id_ein} onChange={set("tax_id_ein")} data-testid="ba-ein-input" placeholder="XX-XXXXXXX" /></Field>
                  <Field label="Form of ownership" testid="ba-ownership">
                    <select className={INPUT} value={form.form_of_ownership} onChange={set("form_of_ownership")} data-testid="ba-ownership-input">
                      <option value="">—</option>
                      {OWNERSHIP.map((o) => <option key={o} value={o}>{o}</option>)}
                    </select>
                  </Field>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <Field label="Owner / officer name" testid="ba-owner"><input className={INPUT} value={form.owner_name} onChange={set("owner_name")} data-testid="ba-owner-input" placeholder="Full name" /></Field>
                  <Field label="Job title" testid="ba-title"><input className={INPUT} value={form.owner_title} onChange={set("owner_title")} data-testid="ba-title-input" placeholder="e.g. Owner, President" /></Field>
                </div>
                <Field label="Physical business address (FFL premises)" testid="ba-address"><input className={INPUT} value={form.premises_address} onChange={set("premises_address")} data-testid="ba-address-input" placeholder="123 Main St, Suite 4" /></Field>
                <div className="grid grid-cols-3 gap-3">
                  <Field label="City" testid="ba-city"><input className={INPUT} value={form.city} onChange={set("city")} data-testid="ba-city-input" /></Field>
                  <Field label="State" testid="ba-state">
                    <select className={INPUT} value={form.state} onChange={set("state")} data-testid="ba-state-input">
                      <option value="">—</option>{US_STATES.map((s) => <option key={s} value={s}>{s}</option>)}
                    </select>
                  </Field>
                  <Field label="ZIP" testid="ba-zip"><input className={INPUT} value={form.zip_code} onChange={set("zip_code")} data-testid="ba-zip-input" inputMode="numeric" /></Field>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <Field label="Years in business" optional testid="ba-years"><input className={INPUT} value={form.years_in_business} onChange={set("years_in_business")} data-testid="ba-years-input" inputMode="numeric" placeholder="e.g. 7" /></Field>
                  <Field label="Business phone" testid="ba-phone"><input className={INPUT} value={form.business_phone} onChange={set("business_phone")} data-testid="ba-phone-input" placeholder="(555) 555-5555" /></Field>
                </div>
                <Field label="Business email" testid="ba-email"><input className={INPUT} value={form.business_email} onChange={set("business_email")} data-testid="ba-email-input" type="email" placeholder="orders@yourshop.com" /></Field>
                <Field label="Website" optional testid="ba-website"><input className={INPUT} value={form.website} onChange={set("website")} data-testid="ba-website-input" placeholder="https://yourshop.com" /></Field>
                <button onClick={saveStep1} disabled={busy} data-testid="business-apply-step1-continue" className="w-full font-heading font-bold uppercase text-base py-3 bg-olive hover:bg-olive-bright disabled:opacity-50 text-white transition-colors">
                  {busy ? "Saving…" : "Save & continue"}
                </button>
              </div>
            )}

            {step === 2 && (
              <div className="space-y-4" data-testid="business-apply-step-2">
                <div className="border border-[#3E3F42] bg-[#0D0E0F] p-4">
                  <p className={`${LABEL} mb-2`}>Account type requested</p>
                  <div className="grid sm:grid-cols-2 gap-3">
                    {[["firearms_accessories", "Firearms & Accessories", "List firearms, optics and accessories. Requires a valid FFL."],
                      ["accessories_only", "Accessories Only", "List optics and accessories only. No FFL required."]].map(([val, title, blurb]) => (
                      <button key={val} type="button" onClick={() => set("account_type_requested")(val)} data-testid={`ba-acct-${val}`}
                        className={`text-left border p-3 transition-colors ${form.account_type_requested === val ? "border-olive-bright bg-olive/10" : "border-[#3E3F42] hover:border-[#57682A]"}`}>
                        <p className="font-heading text-sm font-bold uppercase text-[#E8E8EA]">{title}</p>
                        <p className="text-xs text-[#8B8D90] mt-1 font-body leading-relaxed">{blurb}</p>
                      </button>
                    ))}
                  </div>
                </div>

                <div className={`border p-4 space-y-4 ${isFirearms ? "border-olive/40" : "border-[#3E3F42]"}`}>
                  <p className="font-mono text-[11px] uppercase tracking-wide text-[#8B8D90]">FFL / SOT verification {!isFirearms && <span className="normal-case text-[#6B6D70]">(optional for Accessories Only)</span>}</p>

                  <div data-testid="ba-ffl-types">
                    <p className={LABEL}>FFL types held <span className="normal-case text-[#6B6D70]">(check all that apply)</span></p>
                    <div className="mt-2 grid sm:grid-cols-2 gap-2">
                      {FFL_TYPES.map(([v, l]) => (
                        <CheckTile key={v} on={(form.ffl_types || []).includes(v)} label={l} testid={`ba-ffl-type-${v}`} onClick={() => toggleArr("ffl_types", v)} />
                      ))}
                    </div>
                  </div>

                  <div data-testid="ba-sot-classes">
                    <p className={LABEL}>SOT class <span className="normal-case text-[#6B6D70]">(optional — check any you hold)</span></p>
                    <div className="mt-2 grid sm:grid-cols-3 gap-2">
                      {SOT_CLASS_OPTS.map((s) => (
                        <CheckTile key={s} on={(form.sot_classes || []).includes(s)} label={s} testid={`ba-sot-${s.split(" ")[1]}`} onClick={() => toggleArr("sot_classes", s)} />
                      ))}
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-3">
                    <Field label="FFL number" testid="ba-ffl-number"><input className={INPUT} value={form.ffl_number} onChange={set("ffl_number")} data-testid="ba-ffl-number-input" placeholder="X-XX-XXX-XX-XX-XXXXX" /></Field>
                    <Field label="% of sales from firearms" optional testid="ba-pct"><input className={INPUT} value={form.pct_sales_firearms} onChange={set("pct_sales_firearms")} data-testid="ba-pct-input" inputMode="numeric" placeholder="e.g. 40" /></Field>
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <Field label="Licensee name (as on license)" testid="ba-licensee"><input className={INPUT} value={form.licensee_name} onChange={set("licensee_name")} data-testid="ba-licensee-input" /></Field>
                    <Field label="License expiration date" testid="ba-exp"><input className={INPUT} value={form.expiration_date} onChange={set("expiration_date")} data-testid="ba-exp-input" type="date" /></Field>
                  </div>
                  <div>
                    <label className={LABEL}>FFL license upload <span className="normal-case text-[#6B6D70]">(PDF, JPG or PNG — max 15MB)</span></label>
                    <input ref={fileRef} type="file" accept=".pdf,.jpg,.jpeg,.png,.webp,.heic" onChange={onPickFile} className="hidden" data-testid="ba-ffl-file-input" />
                    <button type="button" onClick={() => fileRef.current?.click()} data-testid="ba-ffl-file-btn" className="mt-1 w-full inline-flex items-center justify-center gap-2 border border-dashed border-[#3E3F42] hover:border-[#57682A] text-[#8B8D90] hover:text-[#E8E8EA] px-3 py-3 font-mono text-xs uppercase transition-colors">
                      <Upload className="w-4 h-4" /> {docFile ? docFile.name : (ffl && ffl.has_document ? `On file: ${ffl.document_filename || "license"} — replace` : "Choose file")}
                    </button>
                    <p className="mt-1.5 flex items-center gap-1.5 text-[11px] text-[#6B6D70] font-body"><Lock className="w-3 h-3" /> Stored privately. Only OpticSwap compliance admins can view it; every view is logged.</p>
                  </div>
                </div>

                {/* Repeatable: additional licenses (2nd FFL premises, state dealer license, ITAR, etc.) */}
                <div className="border border-[#3E3F42] p-4 space-y-3" data-testid="ba-addl-licenses">
                  <div>
                    <p className="font-mono text-[11px] uppercase tracking-wide text-[#8B8D90]">Additional licenses <span className="normal-case text-[#6B6D70]">(optional)</span></p>
                    <p className="text-xs text-[#6B6D70] mt-1 font-body leading-relaxed">Hold a second FFL at another premises, a state dealer license, or another relevant permit? Add each one here with its own document.</p>
                  </div>

                  {addlLicenses.length > 0 && (
                    <div className="space-y-2" data-testid="ba-addl-list">
                      {addlLicenses.map((l) => (
                        <div key={l.license_id} data-testid={`ba-addl-row-${l.license_id}`} className="flex items-start justify-between gap-3 border border-[#3E3F42] bg-[#0D0E0F] px-3 py-2.5">
                          <div className="min-w-0">
                            <p className="text-sm text-[#E8E8EA] font-body truncate">{l.license_type}</p>
                            <p className="font-mono text-[11px] text-[#8B8D90] mt-0.5">
                              {[l.license_number, l.issuing_authority, l.expiration_date].filter(Boolean).join(" · ") || "No details"}
                            </p>
                            {l.has_document && (
                              <p className="font-mono text-[10px] text-olive-bright mt-0.5 flex items-center gap-1"><FileCheck2 className="w-3 h-3" /> {l.document_filename || "Document attached"}</p>
                            )}
                          </div>
                          <button type="button" onClick={() => removeLicense(l.license_id)} data-testid={`ba-addl-remove-${l.license_id}`}
                            className="shrink-0 text-[#8B8D90] hover:text-blaze transition-colors" title="Remove">
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                      ))}
                    </div>
                  )}

                  <div className="border border-dashed border-[#3E3F42] p-3 space-y-3" data-testid="ba-addl-form">
                    <div className="grid grid-cols-2 gap-3">
                      <Field label="License type" testid="ba-addl-type">
                        <select className={INPUT} value={newLic.license_type} onChange={setLic("license_type")} data-testid="ba-addl-type-input">
                          <option value="">—</option>
                          {ADDL_LICENSE_TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
                        </select>
                      </Field>
                      <Field label="License number" optional testid="ba-addl-number"><input className={INPUT} value={newLic.license_number} onChange={setLic("license_number")} data-testid="ba-addl-number-input" /></Field>
                    </div>
                    {newLic.license_type === "Other" && (
                      <Field label="Describe the license" testid="ba-addl-other"><input className={INPUT} value={newLic.license_type_other} onChange={setLic("license_type_other")} data-testid="ba-addl-other-input" placeholder="e.g. Curio & Relic permit" /></Field>
                    )}
                    <div className="grid grid-cols-2 gap-3">
                      <Field label="Issuing authority" optional testid="ba-addl-authority"><input className={INPUT} value={newLic.issuing_authority} onChange={setLic("issuing_authority")} data-testid="ba-addl-authority-input" placeholder="e.g. ATF, State of TX" /></Field>
                      <Field label="Expiration date" optional testid="ba-addl-exp"><input className={INPUT} value={newLic.expiration_date} onChange={setLic("expiration_date")} data-testid="ba-addl-exp-input" type="date" /></Field>
                    </div>
                    <div>
                      <input ref={licFileRef} type="file" accept=".pdf,.jpg,.jpeg,.png,.webp,.heic" onChange={onPickLicFile} className="hidden" data-testid="ba-addl-file-input" />
                      <button type="button" onClick={() => licFileRef.current?.click()} data-testid="ba-addl-file-btn" className="w-full inline-flex items-center justify-center gap-2 border border-dashed border-[#3E3F42] hover:border-[#57682A] text-[#8B8D90] hover:text-[#E8E8EA] px-3 py-2.5 font-mono text-xs uppercase transition-colors">
                        <Upload className="w-4 h-4" /> {newLicFile ? newLicFile.name : "Attach document (optional)"}
                      </button>
                    </div>
                    <button type="button" onClick={addLicense} disabled={addingLic} data-testid="ba-addl-add-btn"
                      className="w-full inline-flex items-center justify-center gap-1.5 border border-olive-bright/60 text-olive-bright hover:bg-olive/10 disabled:opacity-50 font-mono uppercase text-xs px-3 py-2.5 transition-colors">
                      {addingLic ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />} Add another license
                    </button>
                  </div>
                </div>

                <label className="flex items-start gap-2.5 border border-[#3E3F42] bg-[#0D0E0F] p-3 cursor-pointer" data-testid="ba-attestation">
                  <input type="checkbox" checked={form.attestation} onChange={(e) => set("attestation")(e.target.checked)} data-testid="ba-attestation-input" className="mt-0.5 accent-[#57682A] w-4 h-4" />
                  <span className="text-xs text-secondary-foreground font-body leading-relaxed">
                    I certify I am an authorized representative of this business, the information is accurate, and this business holds a valid, current Federal Firearms License in good standing.
                  </span>
                </label>

                <div className="flex gap-3">
                  <button onClick={() => { setStep(1); window.scrollTo(0, 0); }} data-testid="business-apply-step2-back" className="inline-flex items-center gap-1.5 border border-[#3E3F42] hover:border-[#57682A] text-[#8B8D90] hover:text-[#E8E8EA] font-mono uppercase text-xs px-4 py-3 transition-colors"><ArrowLeft className="w-3.5 h-3.5" /> Back</button>
                  <button onClick={saveStep2} disabled={busy} data-testid="business-apply-step2-continue" className="flex-1 font-heading font-bold uppercase text-base py-3 bg-olive hover:bg-olive-bright disabled:opacity-50 text-white transition-colors">{busy ? "Saving…" : "Save & continue"}</button>
                </div>
              </div>
            )}

            {step === 3 && (
              <div className="space-y-4" data-testid="business-apply-step-3">
                <div className="border border-[#3E3F42] bg-[#0D0E0F] p-4 space-y-3">
                  <Row k="Legal business name" v={form.legal_business_name} />
                  <Row k="DBA" v={form.dba} />
                  <Row k="Tax ID / EIN" v={form.tax_id_ein} />
                  <Row k="Form of ownership" v={form.form_of_ownership} />
                  <Row k="Owner / officer" v={`${form.owner_name}${form.owner_title ? ` · ${form.owner_title}` : ""}`} />
                  <Row k="Premises" v={`${form.premises_address}, ${form.city}, ${form.state} ${form.zip_code}`} />
                  <Row k="Years in business" v={form.years_in_business} />
                  <Row k="Phone" v={form.business_phone} />
                  <Row k="Business email" v={form.business_email} />
                  <Row k="Website" v={form.website} />
                </div>
                <div className="border border-[#3E3F42] bg-[#0D0E0F] p-4 space-y-3">
                  <Row k="Account type requested" v={isFirearms ? "Firearms & Accessories" : (form.account_type_requested ? "Accessories Only" : "")} />
                  <Row k="FFL types held" v={(form.ffl_types || []).map((c) => FFL_TYPE_LABEL[c] || c).join(", ")} />
                  <Row k="SOT class" v={(form.sot_classes || []).join(", ")} />
                  <Row k="FFL number" v={form.ffl_number} />
                  <Row k="Licensee name" v={form.licensee_name} />
                  <Row k="Expiration" v={form.expiration_date} />
                  <Row k="% sales from firearms" v={form.pct_sales_firearms} />
                  <Row k="FFL document" v={docFile ? docFile.name : (ffl && ffl.has_document ? (ffl.document_filename || "On file") : "—")} icon={<FileCheck2 className="w-3.5 h-3.5 text-olive-bright" />} />
                </div>
                {addlLicenses.length > 0 && (
                  <div className="border border-[#3E3F42] bg-[#0D0E0F] p-4 space-y-2" data-testid="ba-review-addl">
                    <p className={LABEL}>Additional licenses</p>
                    {addlLicenses.map((l) => (
                      <div key={l.license_id} className="flex items-start justify-between gap-3">
                        <span className="text-sm text-[#E8E8EA] font-body flex items-center gap-1.5"><FileText className="w-3.5 h-3.5 text-[#8B8D90] shrink-0" />{l.license_type}{l.license_number ? ` · ${l.license_number}` : ""}</span>
                        <span className="font-mono text-[11px] text-[#8B8D90] shrink-0">{l.has_document ? "doc ✓" : "no doc"}</span>
                      </div>
                    ))}
                  </div>
                )}
                <div className="flex gap-3">
                  <button onClick={() => { setStep(2); window.scrollTo(0, 0); }} data-testid="business-apply-step3-back" className="inline-flex items-center gap-1.5 border border-[#3E3F42] hover:border-[#57682A] text-[#8B8D90] hover:text-[#E8E8EA] font-mono uppercase text-xs px-4 py-3 transition-colors"><ArrowLeft className="w-3.5 h-3.5" /> Back</button>
                  <button onClick={submitApp} disabled={busy} data-testid="business-apply-submit" className="flex-1 font-heading font-bold uppercase text-base py-3 bg-olive hover:bg-olive-bright disabled:opacity-50 text-white transition-colors">{busy ? "Submitting…" : "Submit application"}</button>
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}

const Row = ({ k, v, icon }) => (
  <div className="flex items-start justify-between gap-4">
    <span className="font-mono text-[11px] uppercase tracking-wide text-[#8B8D90] shrink-0">{k}</span>
    <span className="text-sm text-[#E8E8EA] text-right font-body flex items-center gap-1.5">{icon}{v || "—"}</span>
  </div>
);
